import { useEffect, useRef, useState } from 'react';
import {
  Button,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Page, Progress, WarningPanel } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { headingColor, text } from '@univerus/plugin-products';
import { ReleaseList } from './ReleaseList';
import { formatRelativeDate } from './formatRelativeDate';
import {
  releaseErrorMessage,
  releasesApiRef,
  type ReleaseFeedResponse,
  type ReleaseFilterOption as FilterOption,
} from './api';

/** How many releases the page asks for, and how many "Show more" adds. */
const RELEASE_PAGE_SIZE = 10;

/**
 * Everything the effect needs for one request. Held as a single object so a
 * forced refresh is a distinct state transition rather than a flag that would
 * otherwise stick to the next filter change.
 */
interface FeedQuery {
  repo: string;
  owner: string;
  limit: number;
  nonce: number;
  force: boolean;
}

// The page frame, heading, controls and empty panel copy the Products page
// (plugins/products: shared.tsx and Releases.tsx), so Releases reads as one of
// the same set of pages rather than a stock Backstage page beside them.
const useStyles = makeStyles(theme => ({
  page: {
    padding: theme.spacing(3),
    maxWidth: 1560,
    gridArea: 'pageContent',
    alignSelf: 'start',
    overflowWrap: 'anywhere',
    minWidth: 0,
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    '& a:focus-visible, & button:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 3,
    },
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(2, 2, 10) },
  },
  heading: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  pageTitle: {
    fontSize: text.page,
    fontWeight: 600,
    lineHeight: 1.25,
    letterSpacing: '-.4px',
    color: headingColor(theme),
    [theme.breakpoints.down('xs')]: { fontSize: text.section },
  },
  pageSubtitle: {
    marginTop: theme.spacing(0.75),
    maxWidth: 880,
    fontSize: text.emphasis,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  filter: {
    minWidth: 200,
  },
  status: {
    color: theme.palette.text.secondary,
    fontSize: text.meta,
    lineHeight: 1.5,
    marginBottom: 12,
  },
  // One bordered surface holding flat rows, like the Products tables. A card
  // per release would stack a list of cards on the page.
  panel: {
    padding: theme.spacing(0, 3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: 'none',
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(0, 2) },
  },
  more: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
  empty: {
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: 'none',
    '& h2': {
      fontSize: text.title,
      fontWeight: 600,
      lineHeight: 1.4,
      color: headingColor(theme),
      marginBottom: theme.spacing(1),
    },
    '& p': {
      fontSize: text.body,
      lineHeight: '23px',
      color: theme.palette.text.secondary,
      margin: 0,
    },
    '& button': { marginTop: theme.spacing(2) },
  },
}));

/**
 * Shown in place of the list when there is nothing to list: what happened,
 * and at most one way forward.
 */
const EmptyPanel = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) => {
  const classes = useStyles();
  return (
    <Paper className={classes.empty}>
      <Typography component="h2">{title}</Typography>
      <p>{description}</p>
      {action}
    </Paper>
  );
};

/**
 * Builds the status line above the list: how fresh the feed is, plus a note
 * when it is partial. The feed is served from a server-side cache, so "now"
 * can lag GitHub by up to the cache TTL — saying when it was built is the
 * honest version of that.
 */
function describeFreshness(data: ReleaseFeedResponse): string {
  const age = formatRelativeDate(data.fetchedAt);
  const parts = [age ? `Updated ${age}` : 'Updated just now'];

  if (data.totalCount > data.releases.length) {
    parts.push(`showing ${data.releases.length} of ${data.totalCount}`);
  }
  if (data.failedRepoCount > 0) {
    parts.push(
      `${data.failedRepoCount} of ${data.repoCount} repositories could not be read`,
    );
  }

  return parts.join(' · ');
}

/**
 * One filter dropdown, rendered only when it has something to choose between.
 * A dropdown holding a single option is a control that cannot change anything.
 */
const FilterSelect = ({
  label,
  plural,
  value,
  options,
  onChange,
}: {
  label: string;
  /** Completes the "All …" option, as the homepage's filters word it. */
  plural: string;
  value: string;
  options: FilterOption[];
  onChange: (next: string) => void;
}) => {
  const classes = useStyles();
  // MUI v4 does not associate a TextField's label with the select's display
  // element on its own, which leaves the control unnamed for assistive tech.
  // Wiring labelId by hand is what gives it an accessible name.
  const labelId = `release-filter-${label.toLowerCase()}-label`;

  if (options.length < 2) {
    return null;
  }

  return (
    <TextField
      select
      size="small"
      variant="outlined"
      label={label}
      value={value}
      className={classes.filter}
      onChange={event => onChange(event.target.value)}
      SelectProps={{ displayEmpty: true, labelId }}
      InputLabelProps={{ shrink: true, id: labelId }}
    >
      <MenuItem value="">All {plural}</MenuItem>
      {options.map(option => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

/**
 * The most recent releases across every product in the catalog.
 *
 * Content comes from the `github-releases` backend plugin, which reads GitHub
 * with the server-side App credential — users need no GitHub login of their
 * own, and the feed is cached server-side so this page does not fan out
 * across the catalog on every visit.
 */
export const ReleasesPage = () => {
  const classes = useStyles();
  const releasesApi = useApi(releasesApiRef);

  const [data, setData] = useState<ReleaseFeedResponse | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [query, setQuery] = useState<FeedQuery>({
    repo: '',
    owner: '',
    limit: RELEASE_PAGE_SIZE,
    nonce: 0,
    force: false,
  });
  const listRef = useRef<HTMLDivElement>(null);
  /** How many releases were on screen when "Show more" was pressed. */
  const shownBeforeMore = useRef<number | undefined>();

  // Changing a filter starts the list over. Carrying an expanded limit across
  // would land the user deep into a set they have not seen the top of.
  const setFilter = (key: 'repo' | 'owner', value: string) =>
    setQuery(current => ({
      ...current,
      [key]: value,
      limit: RELEASE_PAGE_SIZE,
      nonce: current.nonce + 1,
      force: false,
    }));

  const clearFilters = () =>
    setQuery(current => ({
      repo: '',
      owner: '',
      limit: RELEASE_PAGE_SIZE,
      nonce: current.nonce + 1,
      force: false,
    }));

  const showMore = () => {
    // aria-disabled rather than disabled, so the focused button keeps focus.
    if (loading || !data) return;
    shownBeforeMore.current = data.releases.length;
    setQuery(current => ({
      ...current,
      limit: current.limit + RELEASE_PAGE_SIZE,
      nonce: current.nonce + 1,
      force: false,
    }));
  };

  const refresh = () =>
    setQuery(current => ({
      ...current,
      nonce: current.nonce + 1,
      force: true,
    }));

  const filtered = Boolean(query.repo || query.owner);
  const canShowMore = Boolean(
    data &&
      data.releases.length < data.totalCount &&
      query.limit < data.maxLimit,
  );

  // After "Show more" loads, focus stays on the button when it is still
  // there. When it is gone (everything is on screen), it is moved to the first
  // newly shown release, so keyboard focus is never dropped to the page.
  // Copied from the entity tab (EntityReleasesContent).
  useEffect(() => {
    const before = shownBeforeMore.current;
    if (loading || before === undefined) return;
    shownBeforeMore.current = undefined;
    if (canShowMore) return;
    const row = listRef.current?.querySelectorAll<HTMLElement>(
      '[data-release-item]',
    )[before];
    row
      ?.querySelector<HTMLElement>('[role="button"], a[href], button')
      ?.focus();
  }, [loading, canShowMore]);

  useEffect(() => {
    let cancelled = false;

    async function fetchReleases() {
      setLoading(true);
      setError(undefined);

      try {
        // refresh bypasses the server-side cache. Safe to spam: the backend
        // single-flights rebuilds, so repeated clicks cost one fan-out.
        const result = await releasesApi.getFeed({
          limit: query.limit,
          repo: query.repo || undefined,
          owner: query.owner || undefined,
          refresh: query.force,
        });

        if (!cancelled) {
          setData(result);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchReleases();
    return () => {
      cancelled = true;
    };
  }, [releasesApi, query]);

  return (
    <Page themeId="tool">
      <div className={classes.page}>
        <div className={classes.heading}>
          <Typography component="h1" className={classes.pageTitle}>
            Releases
          </Typography>
          <Typography className={classes.pageSubtitle}>
            What shipped recently across Univerus products.
          </Typography>
        </div>

        {data && (
          <div className={classes.controls}>
            <FilterSelect
              label="Product"
              plural="products"
              value={query.repo}
              options={data.filters.repos}
              onChange={next => setFilter('repo', next)}
            />
            <FilterSelect
              label="Owner"
              plural="owners"
              value={query.owner}
              options={data.filters.owners}
              onChange={next => setFilter('owner', next)}
            />
            {/* The feed is cached server-side and can trail GitHub by a few
                minutes; this is the one way to close that gap. */}
            <Tooltip title="Check GitHub for newer releases">
              {/* Wrapped: a disabled button fires no events, so the Tooltip
                  needs an element that still does. */}
              <span>
                <IconButton
                  aria-label="Refresh releases"
                  size="small"
                  disabled={loading}
                  onClick={refresh}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        )}

        {/* Only a first load blanks the page. A refresh or filter change keeps
            the current list visible instead of flashing back to a spinner. */}
        {loading && !data && <Progress />}

        {/* A failed page keeps what is already on screen below. */}
        {!loading && error && (
          <WarningPanel
            title="Could not load releases"
            message={releaseErrorMessage(error)}
          />
        )}

        {!error && data && data.releases.length === 0 && filtered && (
          <EmptyPanel
            title="No releases match these filters"
            description="No releases in the current feed match the selected product and owner."
            action={<Button onClick={clearFilters}>Clear filters</Button>}
          />
        )}

        {!error && data && data.releases.length === 0 && !filtered && (
          <EmptyPanel
            title="No releases yet"
            // An empty feed has two very different causes, and saying which
            // one is the difference between "nothing has shipped" and "the
            // catalog is not wired up". Without this they look identical.
            description={
              data.repoCount === 0
                ? 'No catalog components carry a github.com/project-slug annotation, so there is nowhere to read releases from yet.'
                : `Checked ${data.repoCount} ${
                    data.repoCount === 1 ? 'repository' : 'repositories'
                  }${
                    data.failedRepoCount > 0
                      ? ` (${data.failedRepoCount} could not be read)`
                      : ''
                  } and found no published releases.`
            }
          />
        )}

        {/* Stays mounted while more releases load, so the page keeps its
            height and scroll position, and the focused button and any
            expanded notes survive paging. */}
        {data && data.releases.length > 0 && (
          <section aria-label="Latest releases">
            <Typography
              className={classes.status}
              title={new Date(data.fetchedAt).toLocaleString()}
            >
              {describeFreshness(data)}
            </Typography>
            <Paper className={classes.panel}>
              <div ref={listRef} aria-busy={loading}>
                <ReleaseList releases={data.releases} />
              </div>
            </Paper>
            {/* Shown only while there is both something more to show and room
                under the backend's cap, so it never pages into nothing. */}
            {canShowMore && (
              <div className={classes.more}>
                <Button
                  variant="outlined"
                  aria-disabled={loading}
                  onClick={showMore}
                >
                  {loading
                    ? 'Loading…'
                    : `Show ${Math.min(
                        RELEASE_PAGE_SIZE,
                        data.totalCount - data.releases.length,
                      )} more`}
                </Button>
              </div>
            )}
          </section>
        )}
      </div>
    </Page>
  );
};

export default ReleasesPage;
